<div>
  <!-- Error Message -->
  <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
    <i class="bi bi-exclamation-triangle-fill me-2"></i>
    {{ errorMessage }}
    <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
  </div>

  <!-- Date Range Filter -->
  <div class="card border-0 bg-transparent shadow-none mb-4">
    <div class="card-body">
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label fw-semibold">Start Date</label>
          <input
            type="date"
            class="form-control"
            [(ngModel)]="filterStartDate"
            (change)="onFilterChange()"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label fw-semibold">End Date</label>
          <input
            type="date"
            class="form-control"
            [(ngModel)]="filterEndDate"
            (change)="onFilterChange()"
          />
        </div>
        <div class="col-md-4">
          <button
            type="button"
            class="btn btn-outline-primary w-100"
            (click)="loadReportData()"
            [disabled]="loading"
          >
            <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
            <i *ngIf="!loading" class="bi bi-arrow-clockwise me-2"></i>
            Refresh Reports
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="text-center py-5">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="text-muted mt-2">Loading report data...</p>
  </div>

  <!-- Reports Content -->
  <div *ngIf="!loading">
    <!-- Acreage Report Section -->
    <div class="card border-0 bg-transparent shadow-none mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h6 class="mb-0">
          <i class="bi bi-map me-2"></i>
          Acreage Report
        </h6>
        <button
          type="button"
          class="btn btn-sm btn-primary"
          (click)="exportAcreagePdf()"
          [disabled]="exportingAcreage || !acreageData"
        >
          <span *ngIf="exportingAcreage" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!exportingAcreage" class="bi bi-file-earmark-pdf me-2"></i>
          Export PDF
        </button>
      </div>
      <div class="card-body">
        <div *ngIf="acreageData">
          <div class="row mb-3">
            <div class="col-md-4 text-center">
              <div class="fs-4 fw-semibold">{{ formatNumber(acreageData.totalAcreage) }}</div>
              <div class="small text-body-secondary mt-1">Total Acres Sprayed</div>
            </div>
            <div class="col-md-4 text-center">
              <div class="fs-4 fw-semibold">{{ acreageData.totalFlights }}</div>
              <div class="small text-body-secondary mt-1">Total Flights</div>
            </div>
            <div class="col-md-4 text-center">
              <div class="fs-4 fw-semibold">
                {{ formatDate(acreageData.startDate) }} - {{ formatDate(acreageData.endDate) }}
              </div>
              <div class="small text-body-secondary mt-1">Date Range</div>
            </div>
          </div>

          <!-- Compact Summary Tables -->
          <div class="row g-3">
            <div class="col-md-6">
              <h6 class="text-muted mb-2 small">By Drone</h6>
              <div class="table-responsive">
                <table class="table table-sm table-hover small mb-0">
                  <thead>
                    <tr>
                      <th>Drone</th>
                      <th class="text-end">Acreage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngIf="acreageData.byDrone.length === 0">
                      <td colspan="2" class="text-center text-muted">No data</td>
                    </tr>
                    <tr *ngFor="let item of acreageData.byDrone">
                      <td>{{ item.name }}</td>
                      <td class="text-end">{{ formatNumber(item.totalAcreage) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="col-md-6">
              <h6 class="text-muted mb-2 small">By Pilot</h6>
              <div class="table-responsive">
                <table class="table table-sm table-hover small mb-0">
                  <thead>
                    <tr>
                      <th>Pilot</th>
                      <th class="text-end">Acreage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngIf="acreageData.byPilot.length === 0">
                      <td colspan="2" class="text-center text-muted">No data</td>
                    </tr>
                    <tr *ngFor="let item of acreageData.byPilot">
                      <td>{{ item.name }}</td>
                      <td class="text-end">{{ formatNumber(item.totalAcreage) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div *ngIf="!acreageData" class="text-center text-muted py-4">
          No acreage data available for selected period
        </div>
      </div>
    </div>

    <!-- Chemical Usage Report Section -->
    <div class="card border-0 bg-transparent shadow-none">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h6 class="mb-0">
          <i class="bi bi-droplet me-2"></i>
          Chemical Usage Report
        </h6>
        <button
          type="button"
          class="btn btn-sm btn-primary"
          (click)="exportChemicalPdf()"
          [disabled]="exportingChemical || !chemicalData"
        >
          <span *ngIf="exportingChemical" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!exportingChemical" class="bi bi-file-earmark-pdf me-2"></i>
          Export PDF
        </button>
      </div>
      <div class="card-body">
        <div *ngIf="chemicalData && chemicalData.chemicalUsage.length > 0">
          <div class="table-responsive">
            <table class="table table-sm table-hover small mb-0">
              <thead>
                <tr>
                  <th>Chemical Name</th>
                  <th>EPA Number</th>
                  <th class="text-end">Amount Used</th>
                  <th class="text-end">Flights</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let chemical of chemicalData.chemicalUsage">
                  <td>{{ chemical.chemicalName }}</td>
                  <td>{{ chemical.epaNumber || '-' }}</td>
                  <td class="text-end">{{ formatNumber(chemical.totalAmountUsed) }} {{ chemical.unit }}</td>
                  <td class="text-end">{{ chemical.flightCount }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div *ngIf="!chemicalData || chemicalData.chemicalUsage.length === 0" class="text-center text-muted py-4">
          No chemical usage data available for selected period
        </div>
      </div>
    </div>
  </div>
</div>
